/**
 * Owner: Masa Bayu
 * File: AdminConsole.tsx
 * Date:Time: 2026-10-23 09:40
 * Description: §467-D — rangka papan pemuka pentadbir: sidebar 5 seksyen, kepala
 *   seksyen, catatan "monitoring only" dan papar-masa. Konsep visual ikut
 *   dashboard author (token palet + tipografi yang sama, kelas `qxa__*`).
 *   Tiada kawalan tulis dilukis di mana-mana: pentadbir memantau dan punya
 *   insight penuh, tetapi harga/peranan/pemilikan hanya berubah melalui laluan
 *   operasi bertoken di hub (ruling Owner §467d-R).
 */

import { useCallback, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { getMarketplaceLocaleProfile } from '../home/marketplace/marketplace-locale';
import { ADMIN_LABELS, type AdminLabels, type AdminNavId } from './admin-labels';
import { refreshAdminDashboard, useAdminDashboard } from './admin-dashboard';
import { AdminAccessGate } from './AdminAccessGate';
import { AdminOverviewScreen } from './AdminOverviewScreen';
import { AdminPublicationsScreen } from './AdminPublicationsScreen';
import { AdminOrdersScreen } from './AdminOrdersScreen';
import { AdminAccountsScreen } from './AdminAccountsScreen';
import { AdminSystemScreen } from './AdminSystemScreen';
import { Icon } from './AdminIcons';
import { NO_DATA } from './admin-view';
import './admin-console.css';

export const ADMIN_NAV: readonly { id: AdminNavId; path: string }[] = [
  { id: 'overview', path: '/admin' },
  { id: 'publications', path: '/admin/publications' },
  { id: 'orders', path: '/admin/orders' },
  { id: 'accounts', path: '/admin/accounts' },
  { id: 'system', path: '/admin/system' },
];

/** `executive` ialah nama skrin lama — dipetakan ke ringkasan, bukan skrin mati. */
const SECTION_ALIASES: Readonly<Record<string, AdminNavId>> = Object.freeze({
  '': 'overview',
  overview: 'overview',
  executive: 'overview',
  publications: 'publications',
  orders: 'orders',
  accounts: 'accounts',
  system: 'system',
});

export function sectionOf(path: string): AdminNavId | null {
  const raw = path === '/admin' ? '' : path.slice('/admin/'.length);
  const key = raw.split('/')[0] ?? '';
  return SECTION_ALIASES[key] ?? null;
}

function SectionMissing({
  onNavigate,
  la,
}: {
  onNavigate: (p: string) => void;
  la: AdminLabels;
}): ReactElement {
  return (
    <section className="qxa__card" aria-label={la.brand}>
      <h2 className="qxa__card-title">{la.brand}</h2>
      <ul className="qxa__nav qxa__nav--inline" aria-label={la.navAria}>
        {ADMIN_NAV.map((item) => (
          <li key={item.id}>
            <button type="button" className="qxa__btn" onClick={() => onNavigate(item.path)}>
              {la.nav[item.id]}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface AdminConsoleProps {
  readonly path: string;
  readonly onNavigate: (path: string) => void;
}

export function AdminConsole({ path, onNavigate }: AdminConsoleProps): ReactElement {
  const profile = useMemo(() => getMarketplaceLocaleProfile(), []);
  const la = ADMIN_LABELS[profile.language];
  const dash = useAdminDashboard();
  const [busy, setBusy] = useState(false);
  const section = sectionOf(path);

  const onReload = useCallback((): void => {
    setBusy(true);
    void refreshAdminDashboard({ force: true }).finally(() => setBusy(false));
  }, []);

  const view = dash.view;
  const heading =
    section === 'overview'
      ? { title: la.overviewTitle, body: la.overviewBody }
      : section === 'publications'
        ? { title: la.publicationsTitle, body: la.publicationsBody }
        : section === 'orders'
          ? { title: la.ordersTitle, body: la.ordersBody }
          : section === 'accounts'
            ? { title: la.accountsTitle, body: la.accountsBody }
            : section === 'system'
              ? { title: la.systemTitle, body: la.systemBody }
              : { title: la.brand, body: la.subtitle };

  return (
    <AdminAccessGate onNavigate={onNavigate}>
      <div className="qxa">
        <aside className="qxa__side">
          <div className="qxa__brand">
            <span className="qxa__brand-mark" aria-hidden="true">
              <Icon name="shield" size={20} />
            </span>
            <div>
              <p className="qxa__brand-name">{la.brand}</p>
              <p className="qxa__brand-sub">{la.subtitle}</p>
            </div>
          </div>

          <nav aria-label={la.navAria}>
            <ul className="qxa__nav">
              {ADMIN_NAV.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`qxa__nav-btn${section === item.id ? ' qxa__nav-btn--active' : ''}`}
                    aria-current={section === item.id ? 'page' : undefined}
                    onClick={() => onNavigate(item.path)}
                  >
                    <Icon name={item.id} size={18} />
                    <span>{la.nav[item.id]}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <p className="qxa__readonly">{la.readOnlyNote}</p>
        </aside>

        <main className="qxa__main">
          <header className="qxa__head">
            <div>
              <h1 className="qxa__title">{heading.title}</h1>
              <p className="qxa__sub">{heading.body}</p>
            </div>
            <div className="qxa__head-tools">
              <button type="button" className="qxa__btn" onClick={() => onNavigate('/')}>
                <Icon name="arrowLeft" size={16} />
                <span>{la.backHome}</span>
              </button>
              {view === null ? null : (
                <span className="qxa__stamp">
                  {la.actor} · {view.actor === 'token' ? la.actorToken : view.actor}
                </span>
              )}
              <span className="qxa__stamp">
                {la.generatedAt} ·{' '}
                {view === null ? NO_DATA : view.generatedAt.slice(0, 19).replace('T', ' ')}
              </span>
              <button
                type="button"
                className="qxa__btn qxa__btn--primary"
                onClick={onReload}
                disabled={busy}
              >
                <Icon name="refresh" size={16} />
                <span>{la.refresh}</span>
              </button>
            </div>
          </header>

          {view === null || section === null ? (
            <SectionMissing onNavigate={onNavigate} la={la} />
          ) : section === 'overview' ? (
            <AdminOverviewScreen
              view={view}
              la={la}
              language={profile.language}
              onNavigate={onNavigate}
            />
          ) : section === 'publications' ? (
            <AdminPublicationsScreen view={view} la={la} />
          ) : section === 'orders' ? (
            <AdminOrdersScreen view={view} la={la} />
          ) : section === 'accounts' ? (
            <AdminAccountsScreen view={view} la={la} />
          ) : (
            <AdminSystemScreen view={view} la={la} />
          )}
        </main>
      </div>
    </AdminAccessGate>
  );
}
